import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Language context — uilm disabled.
 *
 * The Blocks platform translation service (/uilm/v1/Key/GetUilmFile and
 * /uilm/v1/Key/GetLanguages) returns 404 Not_Found: Application_Not_Found
 * until an application is registered with the Blocks OS portal. Calling
 * those endpoints on a fresh project causes the page to stay in a loading
 * state forever because the language provider blocks rendering until
 * `isLoading` becomes false.
 *
 * For local dev, we skip uilm entirely and rely on the static
 * fallback-bundle.ts (which the i18n init pre-loads). When the platform
 * translations become available, restore the calls below by uncommenting
 * the `useAvailableLanguages` / `useAvailableModules` lines and the
 * effects that read them.
 *
 * The hook lives in ./use-language-context.ts so this file exports only
 * the React component, satisfying Vite's React Fast Refresh rule.
 */

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (language: string, isUserAction?: boolean) => Promise<void>;
  isLoading: boolean;
  availableLanguages: any[];
  availableModules: any[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export { LanguageContext };
export type { LanguageContextType };

interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: string;
  defaultModules?: string[];
}

const translationCache: Record<string, Set<string>> = { 'en-US': new Set(['common', 'auth']) };

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  defaultLanguage = 'en-US',
}) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<string>(defaultLanguage);
  // uilm disabled → never loading, never querying the API for languages.
  const isLoading = false;
  const isInitialized = useRef(false);

  // Apply the user's stored/default language on mount, once.
  useEffect(() => {
    if (isInitialized.current) return;
    const stored = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
    const lang = stored || defaultLanguage;
    setCurrentLanguage(lang);
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    isInitialized.current = true;
  }, [defaultLanguage, i18n]);

  const setLanguage = useCallback(
    async (language: string, isUserAction = true): Promise<void> => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('language', language);
        if (isUserAction) {
          localStorage.setItem('language_user_selected', 'true');
        }
      }
      await i18n.changeLanguage(language);
      setCurrentLanguage(language);
    },
    [i18n]
  );

  const value = useMemo(
    () => ({
      currentLanguage,
      setLanguage,
      isLoading,
      // uilm disabled → empty arrays so any UI list that reads them shows nothing.
      availableLanguages: [],
      availableModules: [],
    }),
    [currentLanguage, setLanguage, isLoading]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

// The useLanguageContext hook lives in a separate file (./use-language-context.ts)
// so this module exports only the React component, satisfying Vite's React
// Fast Refresh requirement (one file = one component).